import type { LabelPosition } from './label.model';

/**
 * Pixel offsets for each label position.
 * These are base offsets that place the label away from the element.
 *
 * For points: Applied directly from the point location.
 * For lines: Applied from the midpoint. Note that `left`/`right` are interpreted
 * as perpendicular to the line direction (relative to start → end points).
 */
const POSITION_OFFSETS: Record<LabelPosition, [number, number]> = {
  top: [0, 15],
  bottom: [0, -15],
  left: [-15, 0],
  right: [15, 0],
  topLeft: [-10, 10],
  topRight: [10, 10],
  bottomLeft: [-10, -10],
  bottomRight: [10, -10],
  center: [0, 0],
};

/**
 * Anchor configuration for each label position.
 * Determines how the label text box is aligned relative to its position.
 */
const POSITION_ANCHORS: Record<LabelPosition, { anchorX: string; anchorY: string }> = {
  top: { anchorX: 'middle', anchorY: 'bottom' },
  bottom: { anchorX: 'middle', anchorY: 'top' },
  left: { anchorX: 'right', anchorY: 'middle' },
  right: { anchorX: 'left', anchorY: 'middle' },
  topLeft: { anchorX: 'right', anchorY: 'bottom' },
  topRight: { anchorX: 'left', anchorY: 'bottom' },
  bottomLeft: { anchorX: 'right', anchorY: 'top' },
  bottomRight: { anchorX: 'left', anchorY: 'top' },
  center: { anchorX: 'middle', anchorY: 'middle' },
};

/**
 * Get the pixel offset for a label position.
 *
 * @param position - The label position
 * @returns [dx, dy] pixel offsets
 */
export function getPositionOffset(position: LabelPosition): [number, number] {
  return POSITION_OFFSETS[position];
}

/**
 * Get the anchor configuration for a label position.
 * The anchor determines how the label is aligned relative to its position.
 *
 * @param position - The label position
 * @returns Anchor configuration for JSXGraph text element
 */
export function getAnchor(position: LabelPosition): { anchorX: string; anchorY: string } {
  return POSITION_ANCHORS[position];
}

/**
 * Combine position offset with custom offset.
 * The custom offset allows fine-tuning of label placement beyond the base position.
 *
 * @param position - The label position (determines base offset)
 * @param customOffset - Additional [dx, dy] offset in pixels for fine-tuning
 * @returns Combined [dx, dy] pixel offsets
 *
 * @example
 * // Position 'right' with additional 5px down adjustment
 * getCombinedOffset('right', [0, -5]); // Returns [15, -5]
 */
export function getCombinedOffset(position: LabelPosition, customOffset: [number, number]): [number, number] {
  const positionOffset = getPositionOffset(position);
  return [positionOffset[0] + customOffset[0], positionOffset[1] + customOffset[1]];
}
